import { DATA_KEYS, PRODUCTIVITY_HOURS_PER_USER_PER_WEEK, TONE, getToneFromScore, getToneFromStatus } from './constants'

const COMING_SOON = 'coming_soon'
const NOT_ASSESSED = 'not_assessed'
const PENDING_GUEST_STATE = 'pendingacceptance'

const getSafeArray = (value) => (Array.isArray(value) ? value : [])

export const getDomainScore = (domainScores = [], scoreId) => {
  const score = domainScores.find((entry) => entry.scoreId === scoreId)
  if (!score) return { value: null, tone: TONE.NEUTRAL, isUnavailable: true }

  const isUnavailable = score.fallbackValue === COMING_SOON || score.fallbackValue === NOT_ASSESSED

  return {
    value: isUnavailable ? null : score.value,
    tone: isUnavailable ? TONE.NEUTRAL : getToneFromStatus(score.status),
    isUnavailable,
  }
}

export const getAvailabilityByDataKey = (dataAvailability = []) =>
  Object.fromEntries(dataAvailability.map((entry) => [entry.dataKey, entry]))

export const getDataGap = (dataAvailability = [], dataKeys = []) => {
  const availabilityByKey = getAvailabilityByDataKey(dataAvailability)
  const gap = dataKeys.map((dataKey) => availabilityByKey[dataKey]).find((entry) => entry?.status !== 'assessed')

  if (!gap) return null

  return {
    status: gap.status,
    reason: gap.reason,
    requiredLicense: gap.requiredLicense,
  }
}

export const getEligibleSeats = (summary) =>
  summary?.licensing?.copilot?.eligibleSeats ?? summary?.licensing?.eligibleBase?.assignedSeats ?? 0
export const getCopilotAssignedSeats = (summary) => summary?.licensing?.copilot?.assignedSeats ?? 0
export const getCopilotPurchasedSeats = (summary) => summary?.licensing?.copilot?.purchasedSeats ?? 0
export const getLicensedUsers = (summary) => summary?.licensing?.licensedUsers ?? summary?.users?.licensed ?? 0

const getCopilotUserLicenseCounts = (summary) => summary?.licensing?.copilot?.userLicenseCounts ?? {}
const getCountOrFallback = (count, fallback) => (Number.isFinite(count) ? count : fallback)

export const getEligibleUsersWithoutSeat = (summary) => {
  const fallback = Math.max(0, getEligibleSeats(summary) - getCopilotAssignedSeats(summary))
  return getCountOrFallback(getCopilotUserLicenseCounts(summary).eligibleWithoutAddOn, fallback)
}

export const getEligibleCoveragePct = (summary) => {
  const licensed = getLicensedUsers(summary)
  if (!licensed) return null
  return Math.min(100, Math.round((getEligibleSeats(summary) / licensed) * 100))
}

export const getAddOnCoveragePct = (summary) => {
  const eligible = getEligibleSeats(summary)
  if (!eligible) return null
  return Math.min(100, Math.round((getCopilotAssignedSeats(summary) / eligible) * 100))
}

export const getEntitiesFlaggedCount = (findings = []) =>
  new Set(getSafeArray(findings).flatMap((finding) => finding.affectedEntityIds || [])).size

export const getFullDeploymentHours = (summary) =>
  Math.round(getLicensedUsers(summary) * PRODUCTIVITY_HOURS_PER_USER_PER_WEEK * 10) / 10

export const getTodayEligibleHours = (summary) =>
  Math.round(getEligibleSeats(summary) * PRODUCTIVITY_HOURS_PER_USER_PER_WEEK * 10) / 10

export const getFindingAffectedCount = (findings = [], findingId) =>
  findings.find((finding) => finding.id === findingId)?.affectedEntityIds?.length ?? 0

export const getGuestAccessScore = (summary = {}, guests = [], dataAvailability = []) => {
  if (getDataGap(dataAvailability, [DATA_KEYS.GUEST_USERS])) return null
  if (summary.users?.guests?.pending === null || summary.users?.guests?.accepted === null) return null

  const total = summary.users?.guests?.total ?? guests.length
  if (total === 0) return 100

  const pending =
    summary.users?.guests?.pending ??
    guests.filter((guest) => String(guest.externalUserState ?? '').toLowerCase() === PENDING_GUEST_STATE).length

  return Math.min(100, Math.max(0, 100 - Math.round((pending / total) * 100)))
}

export const MOCK_BACKED_AREAS = {
  COPILOT_CONFIGURATION: {
    titleId: 'AI_RISK_REPORT.DEPLOYMENT.COPILOT_CONFIG.TITLE',
    dataKeys: [DATA_KEYS.RESTRICTED_SHAREPOINT_SEARCH, DATA_KEYS.COPILOT_STUDIO_AGENTS],
  },
  SHAREPOINT_SITES: {
    titleId: 'AI_RISK_REPORT.DATA.SHAREPOINT.OVERSHARED_TITLE',
    dataKeys: [DATA_KEYS.SHAREPOINT_SITES, DATA_KEYS.SHAREPOINT_PERMISSIONS],
  },
  SENSITIVITY_DLP: {
    titleId: 'AI_RISK_REPORT.DATA.LABELS.TITLE',
    dataKeys: [DATA_KEYS.SENSITIVITY_LABELS],
  },
  DEVICES: {
    titleId: 'AI_RISK_REPORT.IDENTITY.DEVICE.TITLE',
    dataKeys: [DATA_KEYS.MANAGED_DEVICES],
  },
  SHADOW_AI: {
    titleId: 'AI_RISK_REPORT.THIRD.SHADOW_AI.TITLE',
    dataKeys: [DATA_KEYS.EXTERNAL_CONNECTIONS],
  },
}

export const buildMockGap = (dataAvailability, area) =>
  getDataGap(dataAvailability, area.dataKeys) ?? { status: COMING_SOON }

export const getNavScoreValue = (domainScores, scoreId) => {
  const score = getDomainScore(domainScores, scoreId)
  return score.isUnavailable ? null : score.value
}

export const getNavTone = (value) => getToneFromScore(value)
